import React, { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import { reaction } from 'mobx';

import Tooltip from '~/common/components/Tooltip';
import InfoIcon from '~/common/components/InfoIcon2';
import Button from '~/common/components/CommonButton';

import { features } from '~/common/utils/plansFeatures';
import { TRUE_SOLO, SOLO_PLUS, SMALL_BUSINESS, PATH_TRIAL_UPGRADE } from '~/common/utils/consts';
import { scBeacon, createPersonalizedPurchaseEvent, clickCtaEvent, gtm, addToCartEvent, beginCheckoutFlowEvent } from '~/common/utils/tracking.js';

// Import Local Styles
import styles from './features-checklist.css';

@inject('offerSuiteStore', 'convertToPaidStore', 'promoCodeStore', 'lockNumberStore', 'progressStore', 'trialSignupStore', 'modalStore')

@observer
export default class FeaturesChecklist extends Component {
    state = {
        plansDiscounts: {},
        planSets: {}
    }

    componentDidMount() {
        this.setDiscountsAndPlanSets();
    }

    autoDiscountsFetch = reaction(
        () => this.props.promoCodeStore.promoMaxAmount,
        () => {
            this.setDiscountsAndPlanSets();
        }
    )

    // ESC-168 :: Pricing AB Test - Add CTAs with discount offer
    setDiscountsAndPlanSets() {
        const { pairedPlansByTerm } = this.props.offerSuiteStore;

        if (pairedPlansByTerm) {
            const { discounts } = this.props.promoCodeStore;
            const discountsObj = {};
            const plansetsObj = {};
            pairedPlansByTerm.forEach((planSet) => {
                ['annual', 'monthly'].forEach((term) => {
                    const { DescriptiveName, Id } = planSet[term];

                    plansetsObj[DescriptiveName] = Object.assign(plansetsObj[DescriptiveName] || {}, { [term]: planSet[term] });

                    if (discounts[Id]) {
                        discountsObj[DescriptiveName] = Object.assign(discountsObj[DescriptiveName] || {}, { [term]: discounts[Id] });
                    }
                });
            });
            this.setState({ planSets: plansetsObj });
            this.setState({ plansDiscounts: discountsObj });
        }
    }

    renderFeatureCells(feature) {
        const { isTermMonthly } = this.props.offerSuiteStore;

        const cellClassNames = `${styles['feature-data']} ${feature.allCheck ? styles['feature-data--check'] : ''}`;
        const trueSoloProp = feature?.trueSolo;
        const soloPlusProp = feature?.soloPlus;
        const smallBizProp = feature?.smallBiz;
        return (
            <Fragment>
                <th className={styles.feature}>
                    {feature.tooltip ?
                        <Tooltip content={feature.tooltip} contentModifiers={styles.tooltip}>
                            {feature.name} <InfoIcon blue={true} modifier={styles['feature-icon']} />
                        </Tooltip> :
                        <span>
                            {feature.name}
                            {feature.sideNote &&
                                <span className={styles['section-header-note']}>
                                    {feature.sideNote}
                                </span>
                            }
                        </span>
                    }
                    {feature.sideNote && feature.tooltip && <span className={`${styles['feature-note']} ${styles['feature-note--grey']}`}>{feature.sideNote}</span>}
                </th>
                <td className={`${cellClassNames} ${feature?.trueSolo?.highlight ? styles['feature-data--highlight'] : ''} ${trueSoloProp?.check ? styles['feature-data--check'] : ''}`}>
                    {isTermMonthly ? trueSoloProp?.monthlyValue || trueSoloProp?.value : trueSoloProp?.value}
                </td>
                <td className={`${cellClassNames} ${feature?.soloPlus?.highlight ? styles['feature-data--highlight'] : ''} ${soloPlusProp?.check ? styles['feature-data--check'] : ''}`}>
                    {isTermMonthly ? soloPlusProp?.monthlyValue || soloPlusProp?.value : soloPlusProp?.value}
                </td>
                {/* GOTOCOM-6255 :: Partner Values Hidden */}
                {/* <td className={`${cellClassNames} ${feature?.partner?.highlight ? styles['feature-data--highlight'] : ''}`}>
                    {feature?.partner?.value}
                </td> */}
                <td className={`${cellClassNames} ${feature?.smallBiz?.highlight ? styles['feature-data--highlight'] : ''} ${smallBizProp?.check ? styles['feature-data--check'] : ''}`}>
                    {isTermMonthly ? smallBizProp?.monthlyValue || smallBizProp?.value : smallBizProp?.value}
                </td>
            </Fragment>
        );
    }

    // ESC-168 :: Ab Test
    selectOffer = (event, planSet) => {
        const { offerSuiteStore, lockNumberStore, progressStore, convertToPaidStore, trialSignupStore, modalStore } = this.props;
        if (trialSignupStore.isTrialFlow) {
            trialSignupStore.setIsTrialFlow(false);
        }

        // Show Modal for True Solo Trial Conversion
        if ((convertToPaidStore.isUserConvertingToPaid || convertToPaidStore.isTrialPassiveExpired) && planSet[offerSuiteStore.selectedPlanTerm].Name === 'True Solo' && (convertToPaidStore.trialUsersAndExtensions.users > 1 || convertToPaidStore.trialUsersAndExtensions.extensions > 2 || convertToPaidStore.trialUsersAndExtensions.callRecording)) {
            // Prevent redirection to Billing
            event.preventDefault();
            // Show Modal
            modalStore.setModalOptions({
                childComponentString: 'trueSoloTrialConversionModal',
                isModalClosable: false,
                modalType: 'large',
                isModalOpen: true,
                callback: () => {
                    // set selected offer ID in offerSuiteStore
                    offerSuiteStore.setSelectedOfferSet(planSet);
                    // Track Add to Cart
                    addToCartEvent(planSet);
                    gtm(clickCtaEvent('Proceed to checkout', convertToPaidStore.isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans, 'True Solo Trial conversion modal'));
                    gtm(beginCheckoutFlowEvent(1));

                    // track SCBeacon: saw Numbers page first and picked number
                    if (lockNumberStore.selectedNumberUI) scBeacon(createPersonalizedPurchaseEvent('1'));
                }
            });
        } else {
            // set selected offer ID in offerSuiteStore
            offerSuiteStore.setSelectedOfferSet(planSet);
            // Track Add to Cart
            addToCartEvent(planSet);
            gtm(clickCtaEvent(event.target.text, convertToPaidStore.isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans, 'features checklist'));
            gtm(beginCheckoutFlowEvent(1));

            // track SCBeacon: saw Numbers page first and picked number
            if (lockNumberStore.selectedNumberUI) scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    };

    render() {
        const {
            convertToPaidStore: { isUserConvertingToPaid, isTrialExpired },
            offerSuiteStore: { selectedPlanTerm },
            progressStore,
            onTrialSelect,
            promoCodeStore: { isPercentPromo }
        } = this.props;
        const { plansDiscounts, planSets } = this.state;

        return (
            <section className={styles.wrapper}>
                <h3 className={styles.header}>Compare Features</h3>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.feature}>
                            </th>
                            <th className={`${styles.plan}`}>
                                <span className={styles['plan-header']}>{TRUE_SOLO}</span>
                                <span className={styles['plan-offer']}>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-desktop']}`}
                                        onClick={event => this.selectOffer(event, planSets[TRUE_SOLO])}
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                        {plansDiscounts[TRUE_SOLO]?.[selectedPlanTerm] ? `Buy & Save ${isPercentPromo ? `${plansDiscounts[TRUE_SOLO][selectedPlanTerm].discountPercent}%` : `$${plansDiscounts[TRUE_SOLO][selectedPlanTerm].discount}`}` : 'Buy Now'}
                                    </Button>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-mobile']}`}
                                        onClick={event => this.selectOffer(event, planSets[TRUE_SOLO])}
                                        tertiary
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                            Buy Now
                                    </Button>
                                </span>
                            </th>
                            <th className={`${styles.plan}`}>
                                <span className={styles['plan-header']}>{SOLO_PLUS}</span>
                                <span className={styles['plan-offer']}>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-desktop']}`}
                                        onClick={event => this.selectOffer(event, planSets[SOLO_PLUS])}
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                        {plansDiscounts[SOLO_PLUS]?.[selectedPlanTerm] ? `Buy & Save ${isPercentPromo ? `${plansDiscounts[SOLO_PLUS][selectedPlanTerm].discountPercent}%` : `$${plansDiscounts[SOLO_PLUS][selectedPlanTerm].discount}`}` : 'Buy Now'}
                                    </Button>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-mobile']}`}
                                        onClick={event => this.selectOffer(event, planSets[SOLO_PLUS])}
                                        tertiary
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                           Buy Now
                                    </Button>
                                </span>
                            </th>
                            {/* GOTOCOM-6255 :: Partner Values Hidden */}
                            {/* <th className={styles.plan}>
                                {PARTNER}
                            </th> */}
                            <th className={`${styles.plan}`}>
                                <span className={styles['plan-header']}>{SMALL_BUSINESS}</span>
                                <span className={styles['plan-offer']}>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-desktop']}`}
                                        onClick={event => this.selectOffer(event, planSets[SMALL_BUSINESS])}
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                        {plansDiscounts[SMALL_BUSINESS]?.[selectedPlanTerm] ? `Buy & Save ${isPercentPromo ? `${plansDiscounts[SMALL_BUSINESS][selectedPlanTerm].discountPercent}%` : `$${plansDiscounts[SMALL_BUSINESS][selectedPlanTerm].discount}`}` : 'Buy Now'}
                                    </Button>
                                    <Button
                                        modifier={`${styles['plan-cta']} ${styles['cta-mobile']}`}
                                        onClick={event => this.selectOffer(event, planSets[SMALL_BUSINESS])}
                                        tertiary
                                        to={isUserConvertingToPaid ? PATH_TRIAL_UPGRADE : progressStore.nextStepFromPlans}
                                    >
                                            Buy Now
                                    </Button>
                                </span>

                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {features.map((featureSection, index) => {
                            return (<Fragment key={`${index}-${featureSection.name.split(' ').join('-')}-feature-section-header`}>
                                <tr>
                                    <th className={styles['section-header']}>
                                        {featureSection.name}
                                        {featureSection.sideNote &&
                                            <span className={styles['section-header-note']}>
                                                {featureSection.sideNote}
                                            </span>
                                        }
                                    </th>
                                </tr>
                                {featureSection.rows.map((feature) => {
                                    let subFeatures = false;
                                    if (feature.subFeatures) {
                                        subFeatures = feature.subFeatures.map((subFeature, i) =>
                                            (<tr className={`${styles['sub-feature-row']} ${i < feature.subFeatures.length - 1 ? styles['feature-no-border'] : ''}`} key={`${i}-sub-feature`}>
                                                {this.renderFeatureCells(subFeature)}
                                            </tr>)
                                        );
                                    }
                                    return (<Fragment key={`${index}-${feature.name.split(' ').join('-')}-feature`}>
                                        <tr className={feature.subFeatures ? styles['feature-no-border'] : ''}>
                                            {this.renderFeatureCells(feature)}
                                        </tr>
                                        {subFeatures && subFeatures}
                                    </Fragment>
                                    );
                                })}
                            </Fragment>);
                        })}
                    </tbody>
                </table>
                {
                    // hide for convert to paid and international users and trial expired
                    !isUserConvertingToPaid && !isTrialExpired &&
                    <Button
                        modifier={styles['trial-cta']}
                        onClick={onTrialSelect}
                    >Start a Free Trial</Button>
                }
            </section>
        );
    }
}
